import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ModuloCatalogoItem } from '../../config/modulosCatalogo';
import { getModuloMateriales } from '../../config/moduloMateriales';
import { ModuloMasterDto } from '../../services/modulosService';
import ModuloLogo from './ModuloLogo';
import ModuloMaterialDescargas from './ModuloMaterialDescargas';
import { formatCostoMensual } from './moduloFormat';

interface ModuloCardProps {
  item: ModuloCatalogoItem;
  habilitado: boolean;
  master?: ModuloMasterDto;
  mostrarCosto?: boolean;
  toggling?: boolean;
  onToggle: (habilitado: boolean) => void;
  onOpen: () => void;
}

const ModuloCard: React.FC<ModuloCardProps> = ({
  item,
  habilitado,
  master,
  mostrarCosto = false,
  toggling,
  onToggle,
  onOpen,
}) => {
  const costoLabel = formatCostoMensual(master?.costoMensual ?? null, master?.moneda ?? null);
  const tieneMateriales = getModuloMateriales(item.campo).length > 0;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: habilitado ? 'transparent' : 'divider',
        borderLeft: habilitado ? `4px solid ${item.color}` : '4px solid transparent',
        boxShadow: habilitado
          ? '0 2px 10px rgba(0,0,0,0.08)'
          : '0 1px 4px rgba(0,0,0,0.04)',
        opacity: habilitado ? 1 : 0.78,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
          opacity: 1,
        },
      }}
    >
      <CardActionArea
        onClick={onOpen}
        sx={{ flex: 1, alignItems: 'stretch' }}
        component="div"
      >
        <CardContent sx={{ p: 2.5, height: '100%' }}>
          <Stack spacing={1.5} sx={{ height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <ModuloLogo
                campo={item.campo}
                tieneLogo={Boolean(master?.tieneLogo)}
                logoVersion={master?.logoVersion}
                icon={item.icon}
                color={item.color}
                size={52}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {item.nombre}
                  </Typography>
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Switch
                      size="small"
                      checked={habilitado}
                      disabled={toggling}
                      onChange={(_, checked) => onToggle(checked)}
                      color="success"
                      inputProps={{ 'aria-label': `Habilitar ${item.nombre}` }}
                    />
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {item.categoria}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: 40,
              }}
            >
              {item.descripcionCorta}
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 'auto' }}>
              <Chip
                size="small"
                icon={habilitado ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                label={habilitado ? 'Habilitado' : 'Deshabilitado'}
                color={habilitado ? 'success' : 'default'}
                variant={habilitado ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              {mostrarCosto && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {costoLabel}
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      {tieneMateriales && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <ModuloMaterialDescargas campo={item.campo} />
        </Box>
      )}
    </Card>
  );
};

export default ModuloCard;
